import React, { useState, useEffect } from 'react'
import {
  Grid,
  TextField,
  FormControlLabel,
  FormHelperText,
  Chip
} from '@mui/material'
import { styled } from '@mui/material/styles'
import './configureDevice.css'
import { GenXDropdown, type Option } from '../../../components/GenXDropdown'
import { connect, type ConnectedProps } from 'react-redux'
import { alarmMetaData } from '../redux/actionCreators'
import { type AlarmMetaDataState } from '../redux/alarmMetaDataSlice'
import { type RootState } from '../../../store'
import { CustomSwitch } from './EditDevice/ParametersTable'
import errorCrossIcon from '../../../assets/errorCrossIcon.svg'

interface ActionsProps {
  errors?: string[]
  formData: any
  index: number
  handleFormFieldChange: (index: number, field: string, value: string) => void
  handleFormDropdownChange: (
    index: number,
    field: any,
    value: any
  ) => void
}

interface FormFieldProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  multiline?: boolean
  rows?: number
  type?: string
  inptTestID?: string
  disabled?: boolean
  error?: boolean
  required?: boolean
  helperText?: string
}

export const logicalIndicatorsLow: Option[] = [
  { value: 'LESS_THAN', label: '<' },
  { value: 'EQUAL', label: '=' },
  { value: 'NOT_EQUAL', label: '≠' },
  { value: 'LESS_OR_EQUAL', label: '<=' }
]

export const logicalIndicatorsHigh: Option[] = [
  { value: 'GREATER_THAN', label: '>' },
  { value: 'EQUAL', label: '=' },
  { value: 'NOT_EQUAL', label: '≠' },
  { value: 'GREATER_OR_EQUAL', label: '>=' }
]

const CustomTextField = styled(TextField)({
  '& input[type=number]': {
    '-moz-appearance': 'textfield'
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      '-webkit-appearance': 'none',
      margin: 0
    }
})
const FormField: React.FC<FormFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  rows,
  type,
  inptTestID,
  disabled,
  error,
  required,
  helperText
}) => (
  <Grid item xs={12}>
    <CustomTextField
      label={label}
      value={value}
      variant="outlined"
      fullWidth
      placeholder={placeholder}
      multiline={multiline}
      rows={rows}
      type={type ?? 'text'}
      InputLabelProps={{ shrink: true }}
      onChange={(e) => {
        onChange(e.target.value)
      }}
      onKeyDown={(e) => {
        // Prevent arrow keys from changing number input values
        if (type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          e.preventDefault()
        }
      }}
      onWheel={(e) => {
        // Prevent mouse wheel from changing number input values when focused
        if (type === 'number' && document.activeElement === e.target) {
          (e.target as HTMLElement).blur()
        }
      }}
      data-testid={inptTestID ?? ''}
      autoComplete="off"
      disabled={disabled}
      required={required}
      error={error}
      helperText={error === true ? helperText : undefined}
      size="medium"
    />
  </Grid>
)

const AlarmRow = (props: PropsFromRedux & ActionsProps): JSX.Element => {
  const [textColorOptions, setTextColorOptions] = useState<Option[]>([])
  const [severityOptions, setSeverityOptions] = useState<Option[]>([])
  const [alamLabelOptions, setAlamLabelOptions] = useState<Option[]>([])

  useEffect(() => {
    const data = props.alarmMetaDataState.data
    if (data !== null) {
      setTextColorOptions(
        data.alarmTextColors.map((elm: any) => ({
          label: elm.colorName,
          value: elm.alarmTextColorId,
          color: elm.colorValue
        }))
      )

      setSeverityOptions(
        data.alarmSeverities.map((elm: any) => ({
          label: elm.severityLevel,
          value: elm.alarmSeverityId
        }))
      )

      setAlamLabelOptions(
        data.alarmLabels.map((elm: any) => ({
          label: elm.labelName,
          value: elm.alarmLabelId
        }))
      )
      if (props.formData.alarmLimitTextColorId === null) {
        props.handleFormDropdownChange(
          props.index,
          'alarmLimitTextColorId',
          data.alarmTextColors?.map((elm: any) => ({
            label: elm.colorName,
            value: elm.alarmTextColorId,
            color: elm.colorValue
          }))?.[0]
        )
      }
    }
  }, [props.alarmMetaDataState.data])

  // istanbul ignore next
  const handleFormFieldChange = (field: string, value: string): void => {
    props.handleFormFieldChange(props.index, field, value)
  }
  // istanbul ignore next
  const handleFormDropdownChange = (field: any, option: any): void => {
    props.handleFormDropdownChange(props.index, field, option)
  }

  const handleAlarm = (event: any): void => {
    handleFormFieldChange('isLimitAlarmEnabled', event.target?.checked)
  }

  return (
    <>
      <Grid
        item
        container
        display={'inline-flex'}
        justifyContent={'space-around'}
        spacing={2}
      >
        <Grid item xs={12} sm={12} md={2} lg={1} xl={1} >
          <label>Enable Alarm</label>
          <FormControlLabel
            checked={props.formData.isLimitAlarmEnabled}
            onChange={handleAlarm}
            style={{ marginLeft: '0px' }}
            control={<CustomSwitch />}
            label=""
          />
        </Grid>
        <Grid item xs={12} sm={12} md={10} lg={11} xl={11}>
          <Grid container display={'inline-flex'} justifyContent={'flex-start'} spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ minWidth: { lg: '145px' }, maxWidth: { lg: '145px' } }} >
              <label>Threshold Value</label>
              <FormField
                type="number"
                value={props.formData.alarmLimit}
                error={
                  (props.formData?.alarmLimit === '' ||
                    props.formData?.alarmLimit == null ||
                    Number.isNaN(props?.formData?.alarmLimit)) &&
                  props.formData.isLimitAlarmEnabled
                }
                onChange={(value) => {
                  if (
                    value === '' ||
                    /^-?\d+(\.\d{1,3})?$|^-?0\.\d{1,3}$|^-?\.0$/.test(value)
                  ) {
                    handleFormFieldChange('alarmLimit', value)
                  }
                }}
                placeholder="-"
                inptTestID={`${props.index === 0 ? 'low' : 'higher'}-limit-inpt`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ minWidth: { lg: '175px' }, maxWidth: { lg: '175px' } }}>
              <label>Dashboard Text</label>
              <FormField
                value={props.formData.alarmLimitStateText}
                onChange={(value) => {
                  handleFormFieldChange('alarmLimitStateText', value)
                }}
                placeholder="-"
                inptTestID={`${props.index === 0 ? 'low' : 'higher'}-limit-state`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ overflow: 'visible', minWidth: { lg: '175px' }, maxWidth: { lg: '175px' } }} >
              <label>Operator</label>
              <GenXDropdown
                width={'100%'}
                placeholder={'Select an option'}
                value={props.formData.LimitOperator}
                options={
                  props.index === 0
                    ? logicalIndicatorsLow
                    : props.index === 1
                      ? logicalIndicatorsHigh
                      : []
                }
                onSelect={(e) => {
                  handleFormDropdownChange('LimitOperator', e)
                }}
                data-testId={`alarm-text-color-${props.index}`}
                error={
                  (props.formData.LimitOperator?.value === undefined &&
                    props.formData.isLimitAlarmEnabled === true) ||
                  (props.formData.LimitOperator?.value === undefined &&
                    props.formData.alarmLimit !== null &&
                    props.formData.alarmLimit !== undefined &&
                    props.formData.alarmLimit !== '')
                }
              />
              {typeof props.formData?.LimitOperator?.value === 'string' &&
                props.formData?.LimitOperator?.value !== '' && (
                  <Chip
                    label={props.formData.LimitOperator?.label}
                    onDelete={() => {
                      handleFormDropdownChange('LimitOperator', {
                        value: null,
                        label: 'Select an option'
                      })
                    }}
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#F0F0F0',
                      borderRadius: '4px',
                      border: '1px solid #444',
                      height: '32px',
                      fontFamily: 'Honeywell Sans Web',
                      fontWeight: 500,
                      marginTop: '8px',
                      fontSize: '14px',
                      '.MuiChip-deleteIcon': {
                        color: '#F0F0F0',
                        background: 'none',
                        borderRadius: 0,
                        '&:hover': {
                          color: '#FF5252',
                          background: 'none'
                        }
                      }
                    }}
                  />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ minWidth: { lg: '175px' }, maxWidth: { lg: '175px' } }}>
              <label>Alarm Text Color</label>
              <GenXDropdown
                width={'100%'}
                placeholder={'Select an option'}
                value={props.formData.alarmLimitTextColorId}
                options={textColorOptions}
                onSelect={(e) => {
                  handleFormDropdownChange('alarmLimitTextColorId', e)
                }}
                data-testId={`alarm-text-color-${
                  props.index === 0 ? 'low' : 'high'
                }`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ minWidth: { lg: '175px' }, maxWidth: { lg: '175px' } }}
              data-testid={`${
                props.index === 0 ? 'low' : 'high'
              }-limit-severity-cell`}
            >
              <label>Severity</label>
              <GenXDropdown
                inputId={`${props.index === 0 ? 'low' : 'high'}-limit-severity`}
                width={'100%'}
                placeholder={'Select an option'}
                value={props.formData.alarmLimitSeverityId}
                options={severityOptions}
                onSelect={(e) => {
                  handleFormDropdownChange('alarmLimitSeverityId', e)
                }}
                data-testId={`alarm-severity-${
                  props.index === 0 ? 'lower' : 'higher'
                }`}
                error={
                  props.formData.alarmLimitSeverityId?.value === undefined &&
                  props.formData.isLimitAlarmEnabled
                }
              />
              {typeof props.formData?.alarmLimitSeverityId?.value === 'string' &&
                props.formData?.alarmLimitSeverityId?.value !== '' && (
                  <Chip
                    label={props.formData.alarmLimitSeverityId?.label}
                    onDelete={() => {
                      handleFormDropdownChange('alarmLimitSeverityId', {
                        value: null,
                        label: 'Select an option'
                      })
                    }}
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#F0F0F0',
                      borderRadius: '4px',
                      border: '1px solid #444',
                      height: '32px',
                      fontFamily: 'Honeywell Sans Web',
                      fontWeight: 500,
                      marginTop: '8px',
                      fontSize: '14px',
                      '.MuiChip-deleteIcon': {
                        color: '#F0F0F0',
                        background: 'none',
                        borderRadius: 0,
                        '&:hover': {
                          color: '#FF5252',
                          background: 'none'
                        }
                      }
                    }}
                  />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3} xl={false} sx={{ minWidth: { lg: '175px' }, maxWidth: { lg: '175px' } }}>
              <label>Label</label>
              <GenXDropdown
                width={'100%'}
                placeholder={'Select an option'}
                value={props.formData.alarmLimitLabelId}
                options={alamLabelOptions}
                onSelect={(e) => {
                  handleFormDropdownChange('alarmLimitLabelId', e)
                }}
                data-testId={`alarm-label-${props.index}`}
                error={
                  props.formData.isLimitAlarmEnabled === true &&
                  props.formData.alarmLimitLabelId?.value === undefined
                }
              />
              {typeof props.formData?.alarmLimitLabelId?.value === 'string' &&
                props.formData?.alarmLimitLabelId?.value !== '' && (
                  <Chip
                    label={props.formData.alarmLimitLabelId?.label}
                    onDelete={() => {
                      handleFormDropdownChange('alarmLimitLabelId', {
                        value: null,
                        label: 'Select an option'
                      })
                    }}
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#F0F0F0',
                      borderRadius: '4px',
                      border: '1px solid #444',
                      height: '32px',
                      fontFamily: 'Honeywell Sans Web',
                      fontWeight: 500,
                      marginTop: '8px',
                      fontSize: '14px',
                      '.MuiChip-deleteIcon': {
                        color: '#F0F0F0',
                        background: 'none',
                        borderRadius: 0,
                        '&:hover': {
                          color: '#FF5252',
                          background: 'none'
                        }
                      }
                    }}
                  />
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={12} lg={6} xl={false} sx={{ minWidth: { lg: '495px', xl: '330px' }, maxWidth: { lg: '495px', xl: '330px' } }} className="recommendationText">
              <label>Recommendation Text</label>
              <FormField
                value={props.formData.alarmLimitRecommendationText}
                onChange={(value) => {
                  handleFormFieldChange('alarmLimitRecommendationText', value)
                }}
                placeholder="Multiple lines of text go here"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}
              className="errorContainer"
              style={{ width: '1px', display: 'grid', minHeight: '50px' }}
            >
              {props?.errors?.map((errorMsg: string, index: number) => (
                <FormHelperText
                  key={index}
                  sx={{
                    color: '#EE3426',
                    paddingLeft: '5px !important',
                    width: '100%',
                    paddingInlineStart: '35px'
                  }}
                  className="error-msg"
                  style={{ verticalAlign: 'middle', display: 'flex' }}
                >
                  <>
                    <img src={errorCrossIcon} alt="Error" width="16" height="16" />
                    <span style={{ marginLeft: '10px' }}> {` ${errorMsg}`}</span>
                  </>
                </FormHelperText>
              ))}
            </Grid>
      </Grid>
    </>
  )
}

interface DispatchToProps {
  alarmMetaData: () => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  alarmMetaData: () => dispatch(alarmMetaData())
})
interface StateToProps {
  alarmMetaDataState: AlarmMetaDataState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  alarmMetaDataState: state.alarmMetaDataState
})

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AlarmRow)
